import { ZomentumEntities } from "@/Contracts";
import { useSelector } from "react-redux";
import {
	AssessmentRepresentationStyle,
	DocumentType,
	QbrRepresentationStyle
} from "@/Contracts/Documents";
import {
	Assessment,
	AssessmentDataLog,
	AssessmentDataType,
	AssessmentIssue,
	AssessmentLikelihood,
	AssessmentRiskLevel,
	AssessmentSeverity,
	AssessmentVulnerabilityData,
	AssessmentVulnerabilityDataLog,
	CloudAssessmentType,
	IssueStatusEnum
} from "@/Contracts/Assessments";
import { HealthStatusDataLog, Qbr } from "@/Contracts/QBR";
import { getRiskFromSeverityLikelihood } from "@/V2Utils";
import { GlobalState } from "@/Reducers/interface";

export default function useEntityData() {
	const entityData = useSelector((state: GlobalState) => {
		let categoryList,
			currentEntityType: ZomentumEntities | null = null,
			manualIssuesList: AssessmentIssue[] = [],
			ms365AutoAssmtIssuesList: AssessmentIssue[] = [],
			representationStyle:
				| AssessmentRepresentationStyle
				| QbrRepresentationStyle
				| null = null,
			currentActiveEntity: Assessment | Qbr | null = null,
			categoryNamesList: string[] = [],
			isLogLoading = false,
			isEntityLoading = false;
		const legendLabels = {
			[AssessmentRiskLevel.LOW]: "Low",
			[AssessmentRiskLevel.MEDIUM]: "Medium",
			[AssessmentRiskLevel.HIGH]: "High"
		};
		const documentType = state.documents.currentActiveDocument
			?.document_type
			? state.documents.currentActiveDocument?.document_type
			: state.templates.currentActiveTemplate?.document_type;
		switch (documentType) {
			case DocumentType.SALES_PRESENTATION:
			case DocumentType.ASSESSMENT: {
				isLogLoading = state.assessments.isAssessmentLogLoading;
				isEntityLoading = state.assessments.isAssessmentLoading;
				currentActiveEntity = state.assessments.currentActiveAssessment;
				categoryList =
					state.assessments.currentActiveAssessment?.issue_types_list;
				currentEntityType = ZomentumEntities.Assessment;

				const allManualAssessmentIssues = (
					state.assessments.currentActiveAssessment
						?.manual_assessment_logs?.data as AssessmentDataLog[]
				)
					?.filter(
						issue => issue.type === AssessmentDataType.Vulnerability
					)
					?.map(issue => issue as AssessmentVulnerabilityDataLog);

				const allMs365Issues = (
					state.assessments.currentActiveAssessment?.assessment_logs
						?.data as AssessmentDataLog[]
				)?.filter(
					issue => issue.type === AssessmentDataType.Vulnerability
				);
				const ms365FilteredIssues = allMs365Issues
					?.filter(issue => {
						const temp = issue.data as AssessmentVulnerabilityData;
						return (
							temp.cloud_assessment_type ===
							CloudAssessmentType.Ms365
						);
					})
					?.map(issue => issue as AssessmentVulnerabilityDataLog);

				// For each category, loop through the order of the issues and extract the correct issues
				// from the complete list of issues and add it to the final list to be returned
				categoryList?.forEach(category => {
					if (category.assessment_data_list?.length > 0) {
						const issues = category.assessment_data_list?.map(
							issue => {
								const currentIssue = ms365FilteredIssues.find(
									ms365Issue => {
										if (category.filter_on_status) {
											return (
												ms365Issue.data?.status ===
													category.filter_on_status &&
												ms365Issue.id === issue
											);
										}
										return ms365Issue.id === issue;
									}
								);
								return {
									id: currentIssue?.id ?? "",
									category:
										currentIssue?.data?.issue_type ?? "",
									issueName:
										currentIssue?.data?.issue_sub_type ??
										"",
									hide: currentIssue?.hide ?? false,
									created:
										currentIssue?.created ?? new Date(),
									updated:
										currentIssue?.updated ?? new Date(),
									type: currentIssue?.type ?? "",
									companyId: currentIssue?.company_id ?? "",
									summary: currentIssue?.data?.summary,
									recommendation:
										currentIssue?.data?.recommendation,
									severity: currentIssue?.data?.severity,
									likelihood: currentIssue?.data?.likelihood,
									riskLevel: getRiskFromSeverityLikelihood({
										severity:
											currentIssue?.data?.severity ??
											AssessmentSeverity.ACCEPTABLE,
										likelihood:
											currentIssue?.data?.likelihood ??
											AssessmentLikelihood.UNLIKELY
									}),
									cloudAssessmentType:
										currentIssue?.data
											?.cloud_assessment_type,
									status:
										currentIssue?.data?.status ??
										IssueStatusEnum.OPEN
								};
							}
						);
						ms365AutoAssmtIssuesList = [
							...ms365AutoAssmtIssuesList,
							...issues
						];
					} else if (
						category.manual_assessment_data_list?.length > 0
					) {
						const issues =
							category.manual_assessment_data_list?.map(issue => {
								const currentIssue =
									allManualAssessmentIssues.find(
										manualAssessmentIssue => {
											if (category.filter_on_status) {
												return (
													manualAssessmentIssue.data
														?.status ===
														category.filter_on_status &&
													manualAssessmentIssue.id ===
														issue
												);
											}
											return (
												manualAssessmentIssue.id ===
												issue
											);
										}
									);
								return {
									id: currentIssue?.id ?? "",
									category:
										currentIssue?.data?.issue_type ?? "",
									issueName:
										currentIssue?.data?.issue_sub_type ??
										"",
									hide: currentIssue?.hide ?? false,
									created:
										currentIssue?.created ?? new Date(),
									updated:
										currentIssue?.updated ?? new Date(),
									type: currentIssue?.type ?? "",
									companyId: currentIssue?.company_id ?? "",
									summary: currentIssue?.data?.summary,
									recommendation:
										currentIssue?.data?.recommendation,
									severity: currentIssue?.data?.severity,
									likelihood: currentIssue?.data?.likelihood,
									riskLevel: getRiskFromSeverityLikelihood({
										severity:
											currentIssue?.data?.severity ??
											AssessmentSeverity.ACCEPTABLE,
										likelihood:
											currentIssue?.data?.likelihood ??
											AssessmentLikelihood.UNLIKELY
									}),
									cloudAssessmentType:
										currentIssue?.data
											?.cloud_assessment_type,
									status:
										currentIssue?.data?.status ??
										IssueStatusEnum.OPEN
								};
							});
						manualIssuesList = [...manualIssuesList, ...issues];
					}
				});

				categoryNamesList =
					state.assessments.currentActiveAssessment?.issue_types_list?.map(
						category => category.issue_type
					) ?? [];
				representationStyle =
					state.assessments.currentActiveAssessment
						?.representation_style ?? null;
				break;
			}
			case DocumentType.QBR: {
				isLogLoading = state.qbr.isQbrLogLoading;
				isEntityLoading = state.qbr.isQbrLoading;
				currentActiveEntity = state.qbr.currentActiveQbr;
				categoryList =
					state.qbr.currentActiveQbr?.health_status_data_list;
				currentEntityType = ZomentumEntities.Qbr;

				const allQbrIssues = (state.qbr.currentActiveQbr
					?.category_qbr_logs?.data || []) as HealthStatusDataLog[];

				// For each category, loop through the order of the issues and extract the correct issues
				// from the complete list of issues and add it to the final list to be returned
				categoryList?.forEach(category => {
					const issues =
						category.sub_category_list?.map(issue => {
							const currentIssue = allQbrIssues.find(qbrIssue => {
								if (category.filter_on_status) {
									return (
										qbrIssue.data?.status ===
											category.filter_on_status &&
										qbrIssue.id === issue
									);
								}
								return qbrIssue.id === issue;
							});
							return {
								id: currentIssue?.id ?? "",
								category: currentIssue?.data?.category ?? "",
								issueName: currentIssue?.data?.issue ?? "",
								hide: currentIssue?.hide ?? false,
								created: currentIssue?.created ?? new Date(),
								updated: currentIssue?.updated ?? new Date(),
								type: currentIssue?.type ?? "",
								companyId: currentIssue?.company_id ?? "",
								budget: currentIssue?.data?.budget,
								severity: currentIssue?.data?.severity,
								likelihood: currentIssue?.data?.likelihood,
								health: currentIssue?.data?.health,
								quarter: currentIssue?.data?.quarter,
								risk: currentIssue?.data?.risk,
								solution: currentIssue?.data?.solution,
								status:
									currentIssue?.data?.status ??
									IssueStatusEnum.OPEN,
								targetYear: currentIssue?.data?.target_year,
								possibleRiskValues:
									currentIssue?.data?.possible_risk_values,
								possibleSolutionValues:
									currentIssue?.data
										?.possible_solution_values,
								possibleStatusValues:
									currentIssue?.data?.possible_status_values
							};
						}) ?? [];
					manualIssuesList = [...manualIssuesList, ...issues];
				});

				categoryNamesList =
					state.qbr.currentActiveQbr?.health_status_data_list?.map(
						category => category.name
					) ?? [];
				representationStyle =
					state.qbr.currentActiveQbr?.representation_style ?? null;
				break;
			}

			default:
				break;
		}
		currentActiveEntity?.legend_labels.forEach(legend => {
			legendLabels[legend.legend_risk_value] = legend.label;
		});
		return {
			categoryList,
			manualIssuesList,
			ms365AutoAssmtIssuesList,
			documentType,
			isLogLoading,
			isEntityLoading,
			categoryNamesList,
			currentActiveEntity,
			representationStyle,
			currentEntityType,
			legendLabels
		};
	});

	return entityData;
}
